import React, { useState, useMemo, useEffect } from "react";
import { useSearchParams, Link } from "react-router-dom";
// import { useMapBridgeOptional } from "../../features/map-bridge/MapBridgeContext";
import GridMap from "../../features/grid/components/GridMap";
import type { PipelineBAData } from "../../features/grid/components/GridMap";
import MetricToggle from "../../features/grid/components/MetricToggle";
import ColorLegend from "../../features/grid/components/ColorLegend";
import TimeSlider from "../../features/grid/components/TimeSlider";
import ZoneDetailPanel from "../../features/grid/components/ZoneDetailPanel";
import ImpactCalculator from "../../features/grid/components/ImpactCalculator";
import { useGridSnapshot } from "../../features/grid/hooks/useGridData";
import { useBaselineSnapshot } from "../../features/baseline/hooks/useBaseline";
import { useBaselineRankings } from "../../features/baseline/hooks/useBaseline";
import { useGeneratorSearch } from "../../features/generators/hooks/useGenerators";
import { STATUS_FILTER_PRESETS, STATUS_LABELS, isPlannedStatus } from "../../api/generators";
import { getStatusBadgeColor, formatCapacity } from "../../features/generators/utils";
import type { GridMetricKey, GridRegionImpact, MetricScale } from "../../api/grid";
import type { BaselineMetric } from "../../api/baseline";

type ViewMode = "live" | "baseline" | "rankings" | "generators";

const TECHNOLOGY_OPTIONS = ["Solar", "Wind", "Natural Gas", "Nuclear", "Hydro", "Coal", "Battery"];

const US_STATES = [
  "AL","AK","AZ","AR","CA","CO","CT","DE","FL","GA","HI","ID","IL","IN","IA",
  "KS","KY","LA","ME","MD","MA","MI","MN","MS","MO","MT","NE","NV","NH","NJ",
  "NM","NY","NC","ND","OH","OK","OR","PA","RI","SC","SD","TN","TX","UT","VT",
  "VA","WA","WV","WI","WY",
];

// Baseline metric keys (subset of GridMetricKey for baseline mode)
const BASELINE_METRIC_MAP: Record<string, GridMetricKey> = {
  carbon_intensity_kg_per_mwh: "carbon_intensity_kg_per_mwh",
  human_health_dalys_per_mwh: "human_health_dalys",
  ecosystem_quality_species_yr_per_mwh: "ecosystem_quality_species_yr",
  resource_scarcity_usd_per_mwh: "resource_scarcity_usd2013",
};

// Reverse map: GridMetricKey → baseline field name
const GRID_TO_BASELINE_KEY: Record<string, string> = {
  carbon_intensity_kg_per_mwh: "carbon_intensity_kg_per_mwh",
  human_health_dalys: "human_health_dalys_per_mwh",
  ecosystem_quality_species_yr: "ecosystem_quality_species_yr_per_mwh",
  resource_scarcity_usd2013: "resource_scarcity_usd_per_mwh",
};

const RANKING_METRICS: { value: BaselineMetric; label: string; unit: string }[] = [
  { value: "carbon_intensity_kg_per_mwh", label: "Carbon Intensity", unit: "kg CO\u2082-eq/MWh" },
  { value: "human_health_dalys_per_mwh", label: "Human Health Impact", unit: "DALYs/MWh" },
  { value: "ecosystem_quality_species_yr_per_mwh", label: "Ecosystem Impact", unit: "species.yr/MWh" },
  { value: "resource_scarcity_usd_per_mwh", label: "Resource Scarcity", unit: "USD/MWh" },
];

function formatRankingValue(val: number | null, metric: BaselineMetric): string {
  if (val == null) return "\u2014";
  const abs = Math.abs(val);
  if (metric === "carbon_intensity_kg_per_mwh") {
    return abs >= 10 ? val.toLocaleString(undefined, { maximumFractionDigits: 0 }) : val.toFixed(1);
  }
  if (metric === "resource_scarcity_usd_per_mwh") {
    return abs >= 100 ? `$${val.toLocaleString(undefined, { maximumFractionDigits: 0 })}` : `$${val.toFixed(2)}`;
  }
  if (abs >= 1_000) return val.toLocaleString(undefined, { maximumFractionDigits: 1 });
  if (abs >= 1) return val.toFixed(2);
  if (abs >= 0.01) return val.toFixed(3);
  if (abs < 0.01 && val !== 0) return val.toExponential(2);
  return val.toFixed(2);
}

function completenessBar(pct: number) {
  const color = pct >= 0.8 ? "bg-green-500" : pct >= 0.5 ? "bg-yellow-500" : "bg-red-500";
  return (
    <div className="flex items-center gap-2">
      <div className="w-16 h-1.5 bg-surface-700 rounded-full overflow-hidden">
        <div className={`h-full ${color} rounded-full`} style={{ width: `${pct * 100}%` }} />
      </div>
      <span className="text-[10px] text-text-muted">{Math.round(pct * 100)}%</span>
    </div>
  );
}

function formatCompact(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`;
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `${(n / 1e3).toFixed(1)}K`;
  return n.toFixed(0);
}

// All metric fields on GridRegionImpact that can be annualized
const ANNUALIZABLE_METRICS: GridMetricKey[] = [
  "carbon_intensity_kg_per_mwh",
  "human_health_dalys",
  "ecosystem_quality_species_yr",
  "resource_scarcity_usd2013",
  "ozone_depletion_kg_cfc11eq",
  "terrestrial_acidification_kg_so2eq",
  "freshwater_eutrophication_kg_peq",
  "marine_eutrophication_kg_neq",
  "human_toxicity_kg_14dceq",
  "photochemical_oxidant_kg_nmvoceq",
  "particulate_matter_kg_pm10eq",
  "terrestrial_ecotoxicity_kg_14dceq",
  "freshwater_ecotoxicity_kg_14dceq",
  "marine_ecotoxicity_kg_14dceq",
  "ionising_radiation_kbq_u235eq",
  "agricultural_land_m2a",
  "urban_land_m2a",
  "natural_land_m2",
  "water_depletion_m3",
  "metal_depletion_kg_feeq",
  "fossil_depletion_kg_oileq",
];

const HOURS_PER_YEAR = 8760;

/** Multiply all metric fields by total_generation_mwh * 8760 to produce annualized values.
 *  total_generation_mwh is the generation for a single hour, so we extrapolate to a full year. */
function annualizeSnapshot(
  source: Map<string, GridRegionImpact>
): Map<string, GridRegionImpact> {
  const result = new Map<string, GridRegionImpact>();
  source.forEach((region, key) => {
    const gen = region.total_generation_mwh || 0;
    const annualGen = gen * HOURS_PER_YEAR;
    const annualized = { ...region };
    for (const metric of ANNUALIZABLE_METRICS) {
      const val = annualized[metric];
      if (val != null) {
        (annualized as any)[metric] = (val as number) * annualGen;
      }
    }
    result.set(key, annualized);
  });
  return result;
}

function parseViewMode(params: URLSearchParams): ViewMode {
  const v = params.get("view");
  if (v === "live" || v === "baseline" || v === "rankings" || v === "generators") return v;
  return "baseline";
}

function GeneratorRow({ g, showBa = true }: { g: import("../../api/generators").GeneratorSummary; showBa?: boolean }) {
  return (
    <a
      href={`/generators/${g.plant_id}/${g.generator_id}`}
      className="block px-4 py-3 border-b border-border/50 hover:bg-surface-700 transition-colors"
    >
      <div className="flex items-start justify-between gap-2">
        <p className="text-body-sm font-medium text-text-primary truncate flex-1">
          {g.plant_name || `${g.plant_id}-${g.generator_id}`}
        </p>
        {g.status && (
          <span className={`inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-medium whitespace-nowrap ${getStatusBadgeColor(g.status)}`}>
            {STATUS_LABELS[g.status] || g.status}
          </span>
        )}
      </div>
      <div className="flex items-center gap-2 mt-1 text-caption text-text-muted">
        <span>{g.technology}</span>
        <span>&bull;</span>
        <span>{g.nameplate_capacity_mw?.toFixed(1)} MW</span>
        <span>&bull;</span>
        <span>{g.state_id}</span>
        {showBa && g.ba_code && (
          <>
            <span>&bull;</span>
            <span className="text-quantum-400">{g.ba_code}</span>
          </>
        )}
      </div>
    </a>
  );
}

const GridMapDashboard: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  // const bridge = useMapBridgeOptional(); // Removed — was causing re-render loop via context

  // Local viewMode state — guarantees immediate re-render on click.
  // Synced with URL for refresh/deep-link support.
  const [viewMode, setViewModeState] = useState<ViewMode>(() => parseViewMode(searchParams));

  // Sync local state when URL changes externally (back/forward, deep link)
  useEffect(() => {
    setViewModeState(parseViewMode(searchParams));
  }, [searchParams]);

  const [selectedMetric, setSelectedMetric] = useState<GridMetricKey>("carbon_intensity_kg_per_mwh");
  const [selectedHour, setSelectedHour] = useState<string | null>(null);
  const [selectedBaCode, setSelectedBaCode] = useState<string | null>(null);
  const [rankingMetric, setRankingMetric] = useState<BaselineMetric>("carbon_intensity_kg_per_mwh");
  const [metricScale, setMetricScale] = useState<MetricScale>("intensity");

  // Generator search state — check URL for ba_code from "See generators in this region" links
  const [generatorSearchText, setGeneratorSearchText] = useState("");
  const [generatorFilters, setGeneratorFilters] = useState<Record<string, string>>(() => {
    const urlBaCode = new URLSearchParams(window.location.search).get("ba_code");
    if (urlBaCode) {
      return {
        ba_code: urlBaCode,
        status: "OP,SB,P,L,T,U,V,TS", // Show all statuses when viewing a region
        technology: "",                  // All technologies
      } as Record<string, string>;
    }
    return {
      status: "P,L,T,U,V,TS",
      technology: "Solar,Wind",
    } as Record<string, string>;
  });
  const [generatorPage, setGeneratorPage] = useState(1);

  // Welcome banner (first visit only)
  const [showWelcome, setShowWelcome] = useState(
    () => !localStorage.getItem("tv_welcome_dismissed")
  );
  const dismissWelcome = () => {
    localStorage.setItem("tv_welcome_dismissed", "1");
    setShowWelcome(false);
  };

  // Impact calculator
  const [showCalculator, setShowCalculator] = useState(false);

  const isGeneratorsMode = viewMode === "generators";

  const generatorParams = useMemo(
    () => ({
      q: generatorSearchText || undefined,
      ba_code: generatorFilters.ba_code || undefined,
      state: generatorFilters.state || undefined,
      technology: generatorFilters.technology || undefined,
      status: generatorFilters.status || undefined,
      page: generatorPage,
      page_size: 50,
    }),
    [generatorSearchText, generatorFilters, generatorPage]
  );

  const {
    data: generatorData,
    isLoading: generatorsLoading,
    error: generatorsError,
  } = useGeneratorSearch(generatorParams, isGeneratorsMode);

  // Live data — only fetch when in live mode (disabled by default since baseline is primary)
  const isLiveMode = viewMode === "live";
  const { data: snapshot, isLoading: liveLoading, error: liveError } = useGridSnapshot(
    isLiveMode ? (selectedHour ?? undefined) : undefined,
    isLiveMode,  // disabled when not in live mode — saves memory + network
  );

  // Baseline + pipeline data — primary data source for the map
  const { data: baselineSnapshot, isLoading: baselineLoading, error: baselineError } = useBaselineSnapshot();

  // Rankings data — only fetch when rankings tab is active
  const { data: rankingsData, isLoading: rankingsLoading } = useBaselineRankings(
    rankingMetric,
    viewMode === "rankings",  // disabled when not viewing rankings
  );

  const setView = (mode: ViewMode) => {
    // Update local state immediately for instant UI response
    setViewModeState(mode);
    setSelectedBaCode(null);
    // Update URL for deep-linking / refresh
    const params = new URLSearchParams(searchParams);
    if (mode === "live") {
      params.delete("view");
    } else {
      params.set("view", mode);
    }
    setSearchParams(params, { replace: true });
  };

  const handleGeneratorFilterChange = (key: string, value: string) => {
    setGeneratorFilters((prev) => {
      const next = { ...prev };
      if (value) next[key] = value;
      else delete next[key];
      return next;
    });
    setGeneratorPage(1);
  };

  const handleGeneratorSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setGeneratorPage(1);
  };

  // Map bridge state reporting removed (was causing re-render loop)

  // Build snapshot map for live mode
  const liveSnapshotMap = useMemo(() => {
    const m = new Map<string, GridRegionImpact>();
    if (snapshot?.regions) {
      for (const region of snapshot.regions) {
        m.set(region.ba_code, region);
      }
    }
    return m;
  }, [snapshot]);

  // Build snapshot map for baseline mode (adapt BaselineSnapshotRegion → GridRegionImpact shape)
  const baselineSnapshotMap = useMemo(() => {
    const m = new Map<string, GridRegionImpact>();
    if (baselineSnapshot?.regions) {
      for (const region of baselineSnapshot.regions) {
        m.set(region.ba_code, {
          ba_code: region.ba_code,
          timestamp: "",
          total_generation_mwh: region.total_generation_mwh ?? 0,
          climate_change_kg_co2eq: 0,
          carbon_intensity_kg_per_mwh: region.carbon_intensity_kg_per_mwh ?? 0,
          human_health_dalys: region.human_health_dalys_per_mwh,
          ecosystem_quality_species_yr: region.ecosystem_quality_species_yr_per_mwh,
          resource_scarcity_usd2013: region.resource_scarcity_usd_per_mwh,
          // Midpoints not available in baseline — set to null
          ozone_depletion_kg_cfc11eq: null,
          terrestrial_acidification_kg_so2eq: null,
          freshwater_eutrophication_kg_peq: null,
          marine_eutrophication_kg_neq: null,
          human_toxicity_kg_14dceq: null,
          photochemical_oxidant_kg_nmvoceq: null,
          particulate_matter_kg_pm10eq: null,
          terrestrial_ecotoxicity_kg_14dceq: null,
          freshwater_ecotoxicity_kg_14dceq: null,
          marine_ecotoxicity_kg_14dceq: null,
          ionising_radiation_kbq_u235eq: null,
          agricultural_land_m2a: null,
          urban_land_m2a: null,
          natural_land_m2: null,
          water_depletion_m3: null,
          metal_depletion_kg_feeq: null,
          fossil_depletion_kg_oileq: null,
        });
      }
    }
    return m;
  }, [baselineSnapshot]);

  // Build pipeline overlay map
  const pipelineByBA = useMemo(() => {
    const m = new Map<string, PipelineBAData>();
    if (baselineSnapshot?.regions) {
      for (const region of baselineSnapshot.regions) {
        if (region.pipeline_generator_count > 0) {
          m.set(region.ba_code, {
            count: region.pipeline_generator_count,
            capacity_mw: region.pipeline_capacity_mw,
            dalys_avoided: region.pipeline_dalys_avoided,
            usd_avoided: region.pipeline_usd_avoided,
          });
        }
      }
    }
    return m;
  }, [baselineSnapshot]);

  // Active snapshot map based on view mode (generators uses baseline as background context)
  const rawSnapshotMap =
    viewMode === "baseline" || viewMode === "generators"
      ? baselineSnapshotMap
      : liveSnapshotMap;

  // Apply annualization if needed
  const activeSnapshotMap = useMemo(() => {
    if (metricScale === "annual") {
      return annualizeSnapshot(rawSnapshotMap);
    }
    return rawSnapshotMap;
  }, [rawSnapshotMap, metricScale]);

  const isLoading =
    viewMode === "live"
      ? liveLoading
      : viewMode === "baseline" || viewMode === "generators"
        ? baselineLoading
        : rankingsLoading;
  const error =
    viewMode === "live"
      ? liveError
      : viewMode === "baseline" || viewMode === "generators"
        ? baselineError
        : null;

  // Compute legend min/max
  const { legendMin, legendMax } = useMemo(() => {
    let min = Infinity;
    let max = -Infinity;
    activeSnapshotMap.forEach((region) => {
      const val = region[selectedMetric];
      if (val != null && isFinite(val as number)) {
        const v = val as number;
        if (v < min) min = v;
        if (v > max) max = v;
      }
    });
    if (!isFinite(min)) min = 0;
    if (!isFinite(max)) max = 1;
    return { legendMin: min, legendMax: max };
  }, [activeSnapshotMap, selectedMetric]);

  // Compute generation-weighted national average for selected metric (for comparison)
  const nationalAverage = useMemo(() => {
    let totalGen = 0;
    let weightedSum = 0;
    // Use raw (non-annualized) snapshot for per-MWh rates
    rawSnapshotMap.forEach((region) => {
      const val = region[selectedMetric] as number | null;
      const gen = region.total_generation_mwh || 0;
      if (val != null && gen > 0) {
        weightedSum += val * gen;
        totalGen += gen;
      }
    });
    return totalGen > 0 ? weightedSum / totalGen : null;
  }, [rawSnapshotMap, selectedMetric]);

  // Find pipeline data for selected BA (baseline mode)
  const selectedPipelineData = useMemo(() => {
    if (!selectedBaCode || !baselineSnapshot?.regions) return undefined;
    return baselineSnapshot.regions.find((r) => r.ba_code === selectedBaCode);
  }, [selectedBaCode, baselineSnapshot]);

  const generatorTotalPages = generatorData
    ? Math.ceil(generatorData.total / generatorData.page_size)
    : 0;

  const showMap = viewMode !== "rankings";

  return (
    <div className="relative h-full w-full overflow-hidden">
      {/* Full-bleed map (shown in live, baseline, and generators modes) */}
      {showMap && (
        <div className="absolute inset-0">
          <GridMap
            key={viewMode}
            snapshotData={activeSnapshotMap}
            selectedMetric={selectedMetric}
            selectedBaCode={selectedBaCode}
            geojsonUrls={[
              "/data/us_ba_boundaries.geojson",
              "/data/entsoe_bidding_zones.geojson",
              "/data/uk_grid_boundary.geojson",
            ]}
            codeExtractor={(feature) => {
              const zoneName = feature.properties?.zoneName || "";
              // US zones: "US-SW-SRP" → "SRP"
              if (zoneName.startsWith("US-")) return zoneName.split("-").pop() || zoneName;
              // EU/UK: zoneName IS the code
              return zoneName;
            }}
            mapCenter={[35, -20]}
            mapZoom={3}
            onZoneClick={(code) => {
              setSelectedBaCode((prev) => (prev === code ? null : code));
              if (viewMode === "generators") {
                const newBa = generatorFilters.ba_code === code ? "" : code;
                setGeneratorFilters((prev) => ({
                  ...prev,
                  ba_code: newBa,
                  // When setting a BA filter, show all statuses/techs; when clearing, restore defaults
                  ...(newBa
                    ? { status: "OP,SB,P,L,T,U,V,TS", technology: "" }
                    : { status: "P,L,T,U,V,TS", technology: "Solar,Wind" }),
                }));
                setGeneratorPage(1);
              }
            }}
            pipelineByBA={undefined}
            showPipelineOverlay={false}
            generators={viewMode === "generators" ? generatorData?.generators : undefined}
            showGeneratorOverlay={viewMode === "generators"}
            focusBaCode={viewMode === "generators" ? (generatorFilters.ba_code || null) : null}
          />
        </div>
      )}

      {/* View mode switcher — top-left, above metric toggle */}
      <div className="absolute top-4 left-4 z-20 flex flex-col gap-2">
        <div className="flex rounded-lg bg-surface-800/90 backdrop-blur-sm border border-border/50 overflow-hidden">
          {(["baseline", "generators", "rankings"] as ViewMode[]).map((mode) => {
            const labels: Record<ViewMode, string> = {
              live: "Live Map",
              baseline: "Grid Intensity",
              generators: "Generators",
              rankings: "Rankings",
            };
            return (
              <button
                key={mode}
                onClick={() => setView(mode)}
                className={`px-3 py-1.5 text-body-sm font-medium transition-colors whitespace-nowrap ${
                  viewMode === mode
                    ? "bg-quantum-500/20 text-quantum-400 border-b-2 border-quantum-500"
                    : "text-text-secondary hover:text-text-primary hover:bg-surface-700"
                }`}
              >
                {labels[mode]}
              </button>
            );
          })}
        </div>

        {/* Metric selector (carbon, health, ecosystem, resource) */}
        {showMap && viewMode === "baseline" && (
          <MetricToggle
            selected={selectedMetric}
            onChange={setSelectedMetric}
          />
        )}
      </div>

      {/* Generator filter bar — below view mode switcher when in generators mode */}
      {viewMode === "generators" && (
        <div className="absolute top-16 left-4 right-[25rem] z-20">
          <div className="flex flex-wrap items-center gap-2 bg-surface-800/90 backdrop-blur-sm border border-border/50 rounded-lg px-3 py-2">
            <form onSubmit={handleGeneratorSearch} className="flex gap-2 flex-1 min-w-[180px]">
              <input
                type="text"
                value={generatorSearchText}
                onChange={(e) => setGeneratorSearchText(e.target.value)}
                placeholder="Search plant or company..."
                className="input flex-1 text-body-sm"
              />
              <button type="submit" className="btn-secondary px-3 py-1 text-body-sm">
                Search
              </button>
            </form>

            <select
              value={generatorFilters.status || ""}
              onChange={(e) => handleGeneratorFilterChange("status", e.target.value)}
              className="input w-40 text-body-sm"
            >
              {STATUS_FILTER_PRESETS.map((p) => (
                <option key={p.value} value={p.value}>
                  {p.label}
                </option>
              ))}
            </select>

            <select
              value={generatorFilters.state || ""}
              onChange={(e) => handleGeneratorFilterChange("state", e.target.value)}
              className="input w-24 text-body-sm"
            >
              <option value="">State</option>
              {US_STATES.map((s) => (
                <option key={s} value={s}>{s}</option>
              ))}
            </select>

            <div className="flex items-center gap-1">
              {TECHNOLOGY_OPTIONS.map((tech) => {
                const activeTechs = (generatorFilters.technology || "").split(",").filter(Boolean);
                const isOn = activeTechs.includes(tech);
                return (
                  <button
                    key={tech}
                    onClick={() => {
                      const next = isOn
                        ? activeTechs.filter((t) => t !== tech)
                        : [...activeTechs, tech];
                      handleGeneratorFilterChange("technology", next.join(","));
                    }}
                    className={`px-2 py-1 rounded text-[11px] font-medium transition-colors ${
                      isOn
                        ? "bg-quantum-500/20 text-quantum-400 border border-quantum-500/50"
                        : "bg-surface-700 text-text-muted border border-border/50 hover:text-text-secondary"
                    }`}
                  >
                    {tech}
                  </button>
                );
              })}
            </div>

            {generatorFilters.ba_code && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded bg-quantum-500/20 text-quantum-400 text-[11px] font-medium border border-quantum-500/50">
                Region: {generatorFilters.ba_code}
                <button
                  onClick={() => {
                    handleGeneratorFilterChange("ba_code", "");
                    // Restore default filters when clearing BA
                    setGeneratorFilters({
                      status: "P,L,T,U,V,TS",
                      technology: "Solar,Wind",
                    });
                  }}
                  className="ml-0.5 hover:text-quantum-300"
                >
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </span>
            )}

            {generatorData && (
              <span className="text-caption text-text-muted">
                {generatorData.total.toLocaleString()} generators
              </span>
            )}
          </div>
        </div>
      )}

      {/* Pipeline hero stats removed — too noisy for primary grid view */}

      {/* Loading overlay */}
      {(isLoading || (viewMode === "generators" && generatorsLoading)) && showMap && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-900/60 z-30">
          <div className="flex items-center gap-3 bg-surface-800 px-6 py-3 rounded-lg border border-border">
            <div className="w-5 h-5 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
            <span className="text-body-sm text-text-primary">
              {viewMode === "generators"
                ? "Loading generators..."
                : viewMode === "baseline"
                  ? "Loading baseline data..."
                  : "Loading grid data..."}
            </span>
          </div>
        </div>
      )}

      {/* Error overlay */}
      {error && showMap && viewMode !== "generators" && (
        <div className="absolute inset-0 flex items-center justify-center bg-surface-900/60 z-30">
          <div className="bg-surface-800 px-6 py-4 rounded-lg border border-health-500/30 max-w-md">
            <p className="text-body-sm text-health-400">
              {viewMode === "baseline"
                ? "Failed to load baseline data. Baselines may not be computed yet."
                : "Failed to load grid data. The grid impact pipeline may not have run yet."}
            </p>
            <p className="text-caption text-text-muted mt-2">
              {error instanceof Error ? error.message : String(error)}
            </p>
          </div>
        </div>
      )}

      {/* Welcome banner — first visit */}
      {showWelcome && showMap && !isLoading && (
        <div className="absolute inset-0 flex items-center justify-center z-35 pointer-events-none">
          <div className="bg-surface-800/95 backdrop-blur-sm border border-border rounded-xl shadow-2xl max-w-md mx-4 p-6 pointer-events-auto">
            <h2 className="text-h3 font-bold text-text-primary mb-2">
              Grid Environmental Impact
            </h2>
            <p className="text-body-sm text-text-secondary leading-relaxed mb-3">
              This map shows the lifecycle environmental impact of U.S. electricity generation — updated hourly. Impact varies dramatically by region: some grids cause 50x more health damage per MWh than others.
            </p>
            <p className="text-body-sm text-text-secondary leading-relaxed mb-4">
              Click any region to explore. Use the metric toggle to switch between carbon, health, ecosystem, and economic impacts.
            </p>
            <button
              onClick={dismissWelcome}
              className="btn-primary px-6 py-2 text-body-sm w-full"
            >
              Explore the Map
            </button>
          </div>
        </div>
      )}

      {/* Impact calculator — bottom-right */}
      {showMap && (
        <div className="absolute bottom-4 right-4 z-20">
          {showCalculator && baselineSnapshot?.regions ? (
            <ImpactCalculator
              baselineRegions={baselineSnapshot.regions}
              selectedBaCode={selectedBaCode}
              onClose={() => setShowCalculator(false)}
            />
          ) : (
            !isGeneratorsMode && (
              <button
                onClick={() => setShowCalculator(true)}
                className="flex items-center gap-2 px-4 py-2 bg-surface-800/90 backdrop-blur-sm border border-border/50 rounded-lg text-body-sm text-text-secondary hover:text-text-primary hover:border-quantum-500/50 transition-colors"
              >
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                </svg>
                What's my impact?
              </button>
            )
          )}
        </div>
      )}

      {/* Color legend — bottom-left (live and baseline, not generators) */}
      {showMap && viewMode !== "generators" && (
        <div className="absolute bottom-20 left-4 z-20">
          <ColorLegend metric={selectedMetric} min={legendMin} max={legendMax} scale={metricScale} />
        </div>
      )}

      {/* Generator map legend — bottom-left */}
      {viewMode === "generators" && (
        <div className="absolute bottom-4 left-4 bg-surface-900/90 backdrop-blur rounded-lg border border-border p-3 z-20">
          <p className="text-[11px] text-text-muted font-medium mb-2">Legend</p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-green-500 opacity-70" />
              <span className="text-[11px] text-text-muted">Operating</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full border border-dashed border-blue-400 opacity-50" />
              <span className="text-[11px] text-text-muted">Planned</span>
            </div>
            <div className="flex items-center gap-2 mt-2 pt-1.5 border-t border-border/50">
              <span className="w-3 h-3 rounded-full bg-yellow-500" />
              <span className="text-[11px] text-text-muted">Solar</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-teal-500" />
              <span className="text-[11px] text-text-muted">Wind</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-purple-500" />
              <span className="text-[11px] text-text-muted">Nuclear</span>
            </div>
            <div className="flex items-center gap-2">
              <span className="w-3 h-3 rounded-full bg-orange-500" />
              <span className="text-[11px] text-text-muted">Gas</span>
            </div>
          </div>
        </div>
      )}

      {/* Time slider — bottom center (live mode only) */}
      {viewMode === "live" && snapshot?.available_hours && (
        <div className="absolute bottom-4 left-1/2 -translate-x-1/2 z-20">
          <TimeSlider
            rangeStart={snapshot.available_hours.start}
            rangeEnd={snapshot.available_hours.end}
            selectedHour={selectedHour}
            onChange={setSelectedHour}
          />
        </div>
      )}

      {/* Zone detail panel — right side (live and baseline, not generators) */}
      {viewMode !== "rankings" && viewMode !== "generators" && selectedBaCode && (
        <ZoneDetailPanel
          baCode={selectedBaCode}
          selectedMetric={selectedMetric}
          selectedHour={viewMode === "live" ? (selectedHour ?? undefined) : undefined}
          onClose={() => setSelectedBaCode(null)}
          pipelineData={viewMode === "baseline" ? selectedPipelineData : undefined}
          isBaselineMode={viewMode === "baseline"}
          metricScale={metricScale}
          nationalAverage={nationalAverage}
          baselineRegionData={activeSnapshotMap.get(selectedBaCode)}
        />
      )}

      {/* Generator list sidebar — right side (generators mode) */}
      {viewMode === "generators" && (
        <div className="absolute top-0 right-0 bottom-0 w-96 z-20 flex flex-col border-l border-border bg-surface-800">
          {/* Summary stats */}
          {generatorData?.summary && (
            <div className="flex items-center gap-3 px-4 py-2 border-b border-border/50 text-caption text-text-muted flex-wrap">
              <span>
                Total: <strong className="text-text-primary">{formatCapacity(generatorData.summary.total_capacity_mw)}</strong>
              </span>
              {Object.entries(generatorData.summary.count_by_status).map(([status, count]) => (
                <span key={status} className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[11px] ${getStatusBadgeColor(status)}`}>
                  {STATUS_LABELS[status] || status}: {count.toLocaleString()}
                </span>
              ))}
            </div>
          )}

          {/* Generator loading */}
          {generatorsLoading && (
            <div className="flex items-center justify-center py-8">
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
                <span className="text-body-sm text-text-primary">Loading...</span>
              </div>
            </div>
          )}

          {/* Generator error */}
          {generatorsError && (
            <div className="p-4">
              <p className="text-body-sm text-health-400">
                Failed to load generators. The EIA data may not be synced yet.
              </p>
            </div>
          )}

          {/* Generator list */}
          <div className="flex-1 overflow-y-auto">
            {generatorFilters.ba_code && generatorData?.generators ? (
              // Grouped view when filtering by BA — impact drivers vs pipeline offset
              (() => {
                const operating = generatorData.generators.filter((g) => g.status === "OP" || g.status === "SB");
                const planned = generatorData.generators.filter((g) => g.status && isPlannedStatus(g.status));
                return (
                  <>
                    {operating.length > 0 && (
                      <>
                        <div className="sticky top-0 px-4 py-2 bg-surface-700/80 backdrop-blur-sm border-b border-health-500/30">
                          <p className="text-[11px] font-semibold text-health-400 uppercase tracking-wider">
                            Impact Drivers ({operating.length})
                          </p>
                          <p className="text-[10px] text-text-muted">Largest operating generators by capacity</p>
                        </div>
                        {operating.map((g) => (
                          <GeneratorRow key={`${g.plant_id}-${g.generator_id}`} g={g} showBa={false} />
                        ))}
                      </>
                    )}
                    {planned.length > 0 && (
                      <>
                        <div className="sticky top-0 px-4 py-2 bg-surface-700/80 backdrop-blur-sm border-b border-green-500/30">
                          <p className="text-[11px] font-semibold text-green-400 uppercase tracking-wider">
                            Pipeline — Offset ({planned.length})
                          </p>
                          <p className="text-[10px] text-text-muted">Planned generators that would reduce regional impact</p>
                        </div>
                        {planned.map((g) => (
                          <GeneratorRow key={`${g.plant_id}-${g.generator_id}`} g={g} showBa={false} />
                        ))}
                      </>
                    )}
                  </>
                );
              })()
            ) : (
              generatorData?.generators.map((g) => (
                <GeneratorRow key={`${g.plant_id}-${g.generator_id}`} g={g} showBa />
              ))
            )}
          </div>

          {/* Pagination */}
          {generatorTotalPages > 1 && (
            <div className="flex items-center justify-center gap-2 py-3 border-t border-border/50">
              <button
                onClick={() => setGeneratorPage((p) => Math.max(1, p - 1))}
                disabled={generatorPage <= 1}
                className="btn-secondary px-3 py-1 text-caption disabled:opacity-50"
              >
                Prev
              </button>
              <span className="text-caption text-text-muted">
                Page {generatorPage} of {generatorTotalPages}
              </span>
              <button
                onClick={() => setGeneratorPage((p) => Math.min(generatorTotalPages, p + 1))}
                disabled={generatorPage >= generatorTotalPages}
                className="btn-secondary px-3 py-1 text-caption disabled:opacity-50"
              >
                Next
              </button>
            </div>
          )}
        </div>
      )}

      {/* Rankings view — full overlay */}
      {viewMode === "rankings" && (
        <div className="absolute inset-0 bg-surface-900 overflow-y-auto">
          <div className="max-w-5xl mx-auto px-6 py-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-h2 font-bold text-text-primary">
                  Grid Impact Rankings
                </h1>
                <p className="text-body-sm text-text-muted mt-1">
                  Balancing authorities ranked by environmental impact intensity (30-day rolling average)
                </p>
              </div>
              <select
                value={rankingMetric}
                onChange={(e) => setRankingMetric(e.target.value as BaselineMetric)}
                className="input w-56 text-body-sm"
              >
                {RANKING_METRICS.map((m) => (
                  <option key={m.value} value={m.value}>
                    Sort by: {m.label}
                  </option>
                ))}
              </select>
            </div>

            {rankingsLoading && (
              <div className="flex items-center justify-center py-20">
                <div className="w-5 h-5 border-2 border-quantum-500 border-t-transparent rounded-full animate-spin" />
              </div>
            )}

            {rankingsData && (
              <>
                <div className="text-caption text-text-muted mb-4">
                  {rankingsData.count} regions ranked by{" "}
                  {RANKING_METRICS.find((m) => m.value === rankingMetric)?.label} &bull; As of{" "}
                  {new Date(rankingsData.as_of).toLocaleDateString()}
                </div>

                <div className="overflow-hidden rounded-xl border border-border">
                  <table className="w-full text-body-sm">
                    <thead>
                      <tr className="bg-surface-800">
                        <th className="text-left px-4 py-3 text-text-muted font-medium w-12">#</th>
                        <th className="text-left px-4 py-3 text-text-muted font-medium">BA Code</th>
                        <th className="text-right px-4 py-3 text-text-muted font-medium">Carbon Intensity</th>
                        <th className="text-right px-4 py-3 text-text-muted font-medium">Health Impact</th>
                        <th className="text-right px-4 py-3 text-text-muted font-medium">Ecosystem Impact</th>
                        <th className="text-right px-4 py-3 text-text-muted font-medium">Resource Scarcity</th>
                        <th className="text-right px-4 py-3 text-text-muted font-medium">Generation</th>
                        <th className="text-center px-4 py-3 text-text-muted font-medium">Data Coverage</th>
                      </tr>
                    </thead>
                    <tbody>
                      {rankingsData.rankings.map((r) => (
                        <tr
                          key={r.ba_code}
                          className="border-t border-border/30 hover:bg-surface-700/30 transition-colors"
                        >
                          <td className="px-4 py-3 text-text-muted font-mono">{r.rank}</td>
                          <td className="px-4 py-3">
                            <Link
                              to={`/grid/${r.ba_code}`}
                              className="text-quantum-400 hover:text-quantum-300 font-medium"
                            >
                              {r.ba_code}
                            </Link>
                          </td>
                          <td className="text-right px-4 py-3 text-text-primary font-mono">
                            {formatRankingValue(r.carbon_intensity_kg_per_mwh, "carbon_intensity_kg_per_mwh")}
                          </td>
                          <td className="text-right px-4 py-3 text-text-primary font-mono">
                            {formatRankingValue(r.human_health_dalys_per_mwh, "human_health_dalys_per_mwh")}
                          </td>
                          <td className="text-right px-4 py-3 text-text-primary font-mono">
                            {formatRankingValue(
                              r.ecosystem_quality_species_yr_per_mwh,
                              "ecosystem_quality_species_yr_per_mwh"
                            )}
                          </td>
                          <td className="text-right px-4 py-3 text-text-primary font-mono">
                            {formatRankingValue(r.resource_scarcity_usd_per_mwh, "resource_scarcity_usd_per_mwh")}
                          </td>
                          <td className="text-right px-4 py-3 text-text-muted">
                            {r.total_generation_mwh != null
                              ? `${(r.total_generation_mwh / 1000).toFixed(0)} GWh`
                              : "\u2014"}
                          </td>
                          <td className="px-4 py-3">{completenessBar(r.data_completeness)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default GridMapDashboard;
